import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../supabaseClient';
import EmployerSignupLayout, {
  NAVY, AMBER, WHITE, FONT_HEAD, FONT_BODY, StepPills,
} from './EmployerSignupLayout';

const inp = {
  width: '100%', padding: '11px 13px', fontSize: '0.95rem',
  border: '1.5px solid #E2E6EB', borderRadius: '8px',
  color: NAVY, outline: 'none', backgroundColor: WHITE,
};
const lbl = { display: 'block', fontSize: '0.82rem', fontWeight: 600, color: NAVY, marginBottom: '5px' };
const fld = { marginBottom: '1.1rem' };
const g2  = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.1rem' };
const card = {
  backgroundColor: WHITE, borderRadius: '16px',
  boxShadow: '0 2px 24px rgba(0,0,0,0.07)',
  padding: '2rem', marginBottom: '1.25rem',
};

export default function EmployerStep1() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [form, setForm] = useState({
    fullName: '', email: '', password: '', confirmPassword: '', phone: '', agreed: false, ageConfirmed: false,
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const [googleHover, setGoogleHover] = useState(false);

  const handleGoogleSignup = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin + '/auth/employer/callback',
        queryParams: { prompt: 'select_account' },
      },
    });
    if (error) alert(error.message);
  };

  const handleContinue = async () => {
    if (form.password !== form.confirmPassword) {
      alert(t('employer.signup.step1.passwordMismatchAlert'));
      return;
    }
    if (!form.agreed) {
      alert(t('worker.signup.step1.agreeAlert'));
      return;
    }
    if (!form.ageConfirmed) {
      alert(t('worker.signup.step1.ageAlert'));
      return;
    }
    const { error } = await supabase.auth.signUp({
      email: form.email,
      password: form.password,
      options: {
        data: { full_name: form.fullName, user_type: 'employer' },
      },
    });
    if (error) { alert(error.message); return; }
    navigate('/employer/signup/company');
  };

  return (
    <EmployerSignupLayout
      step={1}
      tipText={t('employer.signup.step1.tip')}
      onContinue={handleContinue}
    >
      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: AMBER, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: '1rem' }}>
        {t('employer.signup.step1.eyebrow')}
      </div>

      <StepPills current={1} />

      <div style={card}>
        <h1 style={{ fontFamily: FONT_HEAD, fontSize: '2.1rem', color: NAVY, letterSpacing: '0.03em', marginBottom: '0.35rem' }}>
          {t('employer.signup.step1.title')}
        </h1>
        <p style={{ fontSize: '0.9rem', color: 'rgba(13,27,42,0.52)', marginBottom: '1.75rem', lineHeight: 1.5 }}>
          {t('employer.signup.step1.subtitle')}
        </p>

        {/* Google signup */}
        <button
          onClick={handleGoogleSignup}
          onMouseEnter={() => setGoogleHover(true)}
          onMouseLeave={() => setGoogleHover(false)}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: '10px', padding: '12px 16px', marginBottom: '20px',
            backgroundColor: googleHover ? '#F5F6F8' : WHITE,
            border: '1px solid #D1D5DB', borderRadius: '8px',
            cursor: 'pointer', fontFamily: FONT_BODY,
            fontSize: '0.9375rem', fontWeight: 500, color: '#1F2937',
            transition: 'background-color 0.15s ease',
          }}
        >
          <svg width="18" height="18" viewBox="0 0 48 48">
            <path fill="#4285F4" d="M44.5 20H24v8.5h11.8C34.7 33.9 30.1 37 24 37c-7.2 0-13-5.8-13-13s5.8-13 13-13c3.1 0 5.9 1.1 8.1 2.9l6.4-6.4C34.6 4.1 29.6 2 24 2 11.8 2 2 11.8 2 24s9.8 22 22 22c11 0 21-8 21-22 0-1.3-.2-2.7-.5-4z"/>
            <path fill="#34A853" d="M6.3 14.7l7 5.1C15 16.1 19.2 13 24 13c3.1 0 5.9 1.1 8.1 2.9l6.4-6.4C34.6 4.1 29.6 2 24 2c-7.6 0-14.2 4.1-17.7 10.2z" transform="translate(0,2)"/>
            <path fill="#FBBC05" d="M24 46c5.9 0 10.9-2 14.6-5.4l-6.8-5.6C29.8 36.6 27.1 37 24 37c-6.1 0-10.7-3.1-11.8-7.5l-7 5.4C8.2 41.9 15.5 46 24 46z" transform="translate(0,-2)"/>
            <path fill="#EA4335" d="M44.5 20H24v8.5h11.8c-1 3-3.2 5.4-6.2 6.9l6.8 5.6C40.3 37.4 44.5 31.2 44.5 24c0-1.3-.2-2.7-.5-4z"/>
          </svg>
          {t('worker.signup.step1.googleButton')}
        </button>

        {/* Divider */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <div style={{ flex: 1, height: '1px', backgroundColor: '#E5E7EB' }} />
          <span style={{ fontSize: '0.8125rem', color: 'rgba(13,27,42,0.4)', flexShrink: 0 }}>{t('worker.signup.step1.or')}</span>
          <div style={{ flex: 1, height: '1px', backgroundColor: '#E5E7EB' }} />
        </div>

        {/* Full name */}
        <div style={fld}>
          <label style={lbl}>{t('employer.signup.step1.fullNameLabel')}</label>
          <input style={inp} placeholder={t('employer.signup.step1.fullNamePlaceholder')} value={form.fullName} onChange={e => set('fullName', e.target.value)} />
        </div>

        {/* Work email */}
        <div style={fld}>
          <label style={lbl}>{t('employer.signup.step1.workEmailLabel')}</label>
          <input style={inp} type="email" placeholder={t('employer.signup.step1.workEmailPlaceholder')} value={form.email} onChange={e => set('email', e.target.value)} />
        </div>

        {/* Password + Confirm */}
        <div style={g2}>
          <div>
            <label style={lbl}>{t('worker.signup.step1.passwordLabel')}</label>
            <input style={inp} type="password" placeholder={t('worker.signup.step1.passwordPlaceholder')} value={form.password} onChange={e => set('password', e.target.value)} />
          </div>
          <div>
            <label style={lbl}>{t('worker.signup.step1.confirmPasswordLabel')}</label>
            <input style={inp} type="password" placeholder={t('worker.signup.step1.confirmPasswordPlaceholder')} value={form.confirmPassword} onChange={e => set('confirmPassword', e.target.value)} />
          </div>
        </div>

        {/* Phone */}
        <div style={fld}>
          <label style={lbl}>{t('worker.signup.step1.phoneLabel')}</label>
          <input style={inp} type="tel" placeholder={t('employer.signup.step1.phonePlaceholder')} value={form.phone} onChange={e => set('phone', e.target.value)} />
        </div>

        {/* Terms */}
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', fontSize: '0.85rem', color: 'rgba(13,27,42,0.7)', lineHeight: 1.5 }}>
          <input
            type="checkbox"
            checked={form.agreed}
            onChange={e => set('agreed', e.target.checked)}
            style={{ marginTop: '2px', accentColor: AMBER, width: '16px', height: '16px', flexShrink: 0 }}
          />
          <span>
            {t('worker.signup.step1.agreePrefix')}{' '}
            <a href="/terms-and-conditions" target="_blank" rel="noopener noreferrer" style={{ color: AMBER, textDecoration: 'none', fontWeight: 600 }}>{t('common.termsOfService')}</a>
            {' '}{t('worker.signup.step1.agreeAnd')}{' '}
            <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ color: AMBER, textDecoration: 'none', fontWeight: 600 }}>{t('common.privacyPolicy')}</a>
            {t('worker.signup.step1.agreeSuffix') && ` ${t('worker.signup.step1.agreeSuffix')}`}
          </span>
        </label>

        {/* Age confirmation */}
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', fontSize: '0.85rem', color: 'rgba(13,27,42,0.7)', lineHeight: 1.5, marginTop: '12px' }}>
          <input
            type="checkbox"
            checked={form.ageConfirmed}
            onChange={e => set('ageConfirmed', e.target.checked)}
            style={{ marginTop: '2px', accentColor: AMBER, width: '16px', height: '16px', flexShrink: 0 }}
          />
          <span>{t('worker.signup.step1.ageConfirmLabel')}</span>
        </label>
      </div>
    </EmployerSignupLayout>
  );
}
